import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check, Loader2, PenLine, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const sigSrc = (s) => `${api.defaults.baseURL}${s.url.replace(/^\/api/, "")}`;

export function SignaturePad({ signature, onChange, signerName, onDirtyChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(!signature);

  useEffect(() => { onDirtyChange?.(dirty && open); }, [dirty, open, onDirtyChange]);

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.offsetWidth, h = 180;
    c.width = w * ratio; c.height = h * ratio; c.style.height = `${h}px`;
    const ctx = c.getContext("2d"); ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0f172a";
  }, [open]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 0.1, y + 0.1); ctx.stroke(); setDirty(true); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setDirty(false); };

  const save = async () => {
    if (!dirty) return toast.error("Please sign first");
    setSaving(true);
    try {
      const c = canvasRef.current;
      const out = document.createElement("canvas"); out.width = c.width; out.height = c.height;
      const octx = out.getContext("2d"); octx.fillStyle = "#ffffff"; octx.fillRect(0, 0, out.width, out.height); octx.drawImage(c, 0, 0);
      const blob = await new Promise((r) => out.toBlob(r, "image/png"));
      const fd = new FormData(); fd.append("file", new File([blob], `signature-${Date.now()}.png`, { type: "image/png" }));
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ id: data.id, url: data.url, filename: data.filename, signed_by: signerName || "", signed_at: new Date().toISOString() });
      setOpen(false); setDirty(false);
      toast.success("Signature captured");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save signature"); }
    finally { setSaving(false); }
  };

  if (signature && !open) {
    return (
      <div className="space-y-2" data-testid="signature-saved">
        <div className="rounded-lg border border-slate-200 bg-white p-2"><img src={signature.preview || sigSrc(signature)} alt="Signature" className="h-24 w-full object-contain" data-testid="signature-image" /></div>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Signed by <b className="text-slate-800">{signature.signed_by || "—"}</b></span>
          <div className="flex gap-3">
            <button type="button" className="sm-link" onClick={() => setOpen(true)} data-testid="signature-redo">Re-sign</button>
            <button type="button" className="text-rose-600 hover:underline inline-flex items-center gap-1" onClick={() => onChange(null)} data-testid="signature-remove"><X className="h-3 w-3" />Remove</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div className="text-xs text-slate-500 flex items-center gap-1"><PenLine className="h-3.5 w-3.5" />Hand the phone to {signerName || "the person accepting the documents"} to sign below.</div>
      <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-white overflow-hidden">
        <canvas ref={canvasRef} className="w-full touch-none block" data-testid="signature-canvas"
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} />
        <div className="pointer-events-none absolute left-4 right-4 bottom-8 border-b border-slate-300" />
        <div className="pointer-events-none absolute left-4 bottom-2 text-[10px] uppercase tracking-wider text-slate-400">Sign here</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" className="h-11" onClick={clear} data-testid="signature-clear"><Eraser className="h-4 w-4 mr-1.5" />Clear</Button>
        <Button type="button" className="h-11 bg-[#2d7bd0] hover:bg-[#1f5fa8] text-white" onClick={save} disabled={saving || !dirty} data-testid="signature-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" />Accept Signature</>}</Button>
      </div>
      {signature && <button type="button" className="text-xs sm-link" onClick={() => setOpen(false)}>Keep existing signature</button>}
    </div>
  );
}
